import type { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { STATUS, STATUS_VAR } from '../../lib/viz';
import { useHoldWhileLoading } from '../../lib/appReady';
import { Skeleton } from '../ui';

/**
 * A single headline number. Deliberately not a one-bar bar chart: when the data is
 * one current value, a stat tile reads faster and takes less room.
 *
 * With `onClick` the tile is a button (prototype §38): on a list page it filters the table to the rows behind the
 * number (`active` = the filter is on — click again to clear); elsewhere it drills through to them.
 */
export function StatTile({
  label, value, delta, deltaLabel, status, color, icon, hint, onClick, active, compact, loading, wrapHint,
}: {
  label: string;
  value: ReactNode;
  /** Percentage change; sign drives the arrow. */
  delta?: number;
  deltaLabel?: string;
  status?: keyof typeof STATUS;
  /** A colour for the number outside the status set (the accent, violet); wins over `status`. */
  color?: string;
  icon?: ReactNode;
  hint?: ReactNode;
  onClick?: () => void;
  /** The filter this tile stands for is on. */
  active?: boolean;
  /** Smaller type for rows of five or six tiles. */
  compact?: boolean;
  /** The number is still loading: a skeleton stands in for it. */
  loading?: boolean;
  /** Let the hint wrap onto a second line instead of cutting it off (narrow tiles: "Due 05 Nov 2026" on a phone). */
  wrapHint?: boolean;
}) {
  useHoldWhileLoading(loading);
  const positive = (delta ?? 0) >= 0;
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span className={`font-semibold uppercase tracking-wider text-ink-500 ${compact ? 'text-[10px]' : 'text-[10.5px]'}`}>{label}</span>
        {icon && <span className={`grid shrink-0 place-items-center rounded-lg bg-bronze-50 text-bronze-600 ${compact ? 'h-6 w-6' : 'h-7 w-7'}`}>{icon}</span>}
      </div>
      <div className={`flex items-baseline gap-2.5 ${compact ? 'mt-1' : 'mt-1.5'}`}>
        {loading ? <Skeleton className={compact ? 'h-[18px] w-16' : 'h-[22px] w-20'} /> : (
        <span className={`font-bold leading-none tracking-tight tabular-nums text-ink-900 ${compact ? 'text-[18px]' : 'text-[22px]'}`} style={color ? { color } : status ? { color: STATUS_VAR[status] } : undefined}>
          {value}
        </span>
        )}
        {!loading && delta !== undefined && (
          <span className="flex items-center gap-0.5 text-[11.5px] font-medium" style={{ color: positive ? STATUS_VAR.good : STATUS_VAR.critical }}>
            {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      {(deltaLabel || hint) && <p className={`mt-1 text-[11px] text-ink-500 ${wrapHint ? 'break-words' : 'truncate'}`} title={typeof (deltaLabel ?? hint) === 'string' ? String(deltaLabel ?? hint) : undefined}>{deltaLabel ?? hint}</p>}
      {active && <span className="absolute right-2.5 top-2.5 rounded-full bg-bronze-50 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-bronze-600">Filtered</span>}
    </>
  );
  const cls = `relative overflow-hidden rounded-xl border border-ink-200 bg-white text-left ${compact ? 'p-3' : 'p-4'}`;
  if (!onClick) return <div className={cls}>{body}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`${cls} w-full transition hover:border-ink-300 hover:bg-ink-50/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze-500/40 ${active ? 'border-bronze-500 ring-1 ring-bronze-500' : ''}`}>
      {body}
    </button>
  );
}

/** The line under a filtered table that says what is shown, with a way out (prototype sfNote). */
export function FilterNote({ label, count, onClear }: { label: string | null | undefined; count?: number; onClear: () => void }) {
  if (!label) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-bronze-50 px-3 py-1.5 text-[12px] text-ink-700">
      <span>Showing <b>{label}</b>{count !== undefined ? ` — ${count}` : ''}</span>
      <button type="button" onClick={onClear} className="font-medium text-bronze-700 hover:underline">Show everything</button>
    </div>
  );
}
